"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./Pricing.css";
import PricingCard from "./PricingCard";
import Modal from "../../components/layout/Modal";
import {
  getAllPricingData,
  getPaymentLink,
} from "../../services/pricingService";

const COUNTRY_MAP = {
  // North America
  US: "USA",
  CA: "Canada",
  MX: "Mexico",

  // South America
  BR: "Brazil",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  VE: "Venezuela",
  UY: "Uruguay",
  PY: "Paraguay",
  BO: "Bolivia",
  EC: "Ecuador",

  // Europe
  GB: "United Kingdom",
  IE: "Ireland",
  FR: "France",
  DE: "Germany",
  IT: "Italy",
  ES: "Spain",
  PT: "Portugal",
  NL: "Netherlands",
  BE: "Belgium",
  CH: "Switzerland",
  AT: "Austria",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  PL: "Poland",
  CZ: "Czech Republic",
  SK: "Slovakia",
  HU: "Hungary",
  RO: "Romania",
  BG: "Bulgaria",
  GR: "Greece",
  HR: "Croatia",
  SI: "Slovenia",
  RS: "Serbia",
  UA: "Ukraine",
  LT: "Lithuania",
  LV: "Latvia",
  EE: "Estonia",

  // Middle East
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  IL: "Israel",
  TR: "Turkey",
  QA: "Qatar",
  KW: "Kuwait",
  OM: "Oman",
  BH: "Bahrain",
  JO: "Jordan",
  LB: "Lebanon",

  // South Asia
  IN: "India",
  PK: "Pakistan",
  BD: "Bangladesh",
  LK: "Sri Lanka",
  NP: "Nepal",

  // East & Southeast Asia
  CN: "China",
  JP: "Japan",
  KR: "South Korea",
  HK: "Hong Kong",
  TW: "Taiwan",
  SG: "Singapore",
  MY: "Malaysia",
  TH: "Thailand",
  VN: "Vietnam",
  PH: "Philippines",
  ID: "Indonesia",
  KH: "Cambodia",
  LA: "Laos",
  MM: "Myanmar",

  // Oceania
  AU: "Australia",
  NZ: "New Zealand",

  // Africa
  ZA: "South Africa",
  NG: "Nigeria",
  KE: "Kenya",
  GH: "Ghana",
  EG: "Egypt",
  MA: "Morocco",
  DZ: "Algeria",
  TN: "Tunisia",
  ET: "Ethiopia",
  TZ: "Tanzania",
  UG: "Uganda",
  RW: "Rwanda",
};

async function getLocationByIP() {
  try {
    const res = await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
      cache: "no-store",
    });

    const text = await res.text();

    const locLine = text.split("\n").find((line) => line.startsWith("loc="));

    const country = locLine?.split("=")[1];

    return COUNTRY_MAP[country] || "USA";
  } catch (e) {
    console.error("Cloudflare geo failed", e);
    return "USA";
  }
}

export default function Pricing() {
  const { t } = useTranslation();

  const featureTranslationKeys = {
    "Unlimited Profiles": "pricing.features.unlimitedProfiles",
    "Unlimited Roles": "pricing.features.unlimitedRoles",
    "Internal Mobility": "pricing.features.internalMobility",
    "Personalized Upskilling": "pricing.features.personalizedUpskilling",
    "Soft Skills & Job Skills": "pricing.features.softSkillsJobSkills",
    "Leadership Assessment": "pricing.features.leadershipAssessment",
    "LLM/SLM Console Users": "pricing.features.llmSlmConsoleUsers",
    "Assessment Languages": "pricing.features.assessmentLanguages",
    "Console Languages": "pricing.features.consoleLanguages",
    Reports: "pricing.features.reports",
    Reviews: "pricing.features.reviews",
  };
  const [billingCycle, setBillingCycle] = useState("Monthly");
  const [pricingData, setPricingData] = useState([]);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
    showButton: false,
  });
  const [loadingPriceId, setLoadingPriceId] = useState(null);

  const pricingTiers = [
    {
      name: t("pricing.tiers.starter.name"),
      description: t("pricing.tiers.starter.description"),
      monthlyUSD: 1800,
      monthlyINR: 167400,
      annualUSD: 18000,
      annualINR: 1674000,
      recommended: false,
      features: {
        "Unlimited Profiles": true,
        "Unlimited Roles": true,
        "Internal Mobility": true,
        "Personalized Upskilling": true,
        "Soft Skills & Job Skills": true,
        "Leadership Assessment": true,
        "LLM/SLM Console Users": "10",
        "Assessment Languages": "13",
        "Console Languages": "3",
        Reports: "13",
        Reviews: t("pricing.values.quarterly"),
      },
    },
    {
      name: t("pricing.tiers.growth.name"),
      description: t("pricing.tiers.growth.description"),
      monthlyUSD: 3800,
      monthlyINR: 353400,
      annualUSD: 42000,
      annualINR: 3900000,
      recommended: false,
      features: {
        "Unlimited Profiles": true,
        "Unlimited Roles": true,
        "Internal Mobility": true,
        "Personalized Upskilling": true,
        "Soft Skills & Job Skills": true,
        "Leadership Assessment": true,
        "LLM/SLM Console Users": "25",
        "Assessment Languages": "13",
        "Console Languages": "3",
        Reports: "13",
        Reviews: t("pricing.values.quarterly"),
      },
    },
    {
      name: t("pricing.tiers.scale.name"),
      description: t("pricing.tiers.scale.description"),
      monthlyUSD: 6000,
      monthlyINR: 558600,
      annualUSD: 64000,
      annualINR: 5900000,
      recommended: true,
      features: {
        "Unlimited Profiles": true,
        "Unlimited Roles": true,
        "Internal Mobility": true,
        "Personalized Upskilling": true,
        "Soft Skills & Job Skills": true,
        "Leadership Assessment": true,
        "LLM/SLM Console Users": "75",
        "Assessment Languages": "13",
        "Console Languages": "3",
        Reports: "13",
        Reviews: t("pricing.values.quarterly"),
      },
    },
  ];

  {
    /*{
      name: "Enterprise",
      description: "For teams with 1001+ employees",
      monthlyUSD: "Custom",
      monthlyINR: "Custom",
      annualUSD: "Custom",
      annualINR: "Custom",
      recommended: false,
      features: {
        "Unlimited Profiles": true,
        "Unlimited Roles": true,
        "Internal Mobility": true,
        "Personalized Upskilling": true,
        "Soft Skills & Job Skills": true,
        "Leadership Assessment": true,
        "LLM/SLM Console Users": "Unlimited Users",
        "Assessment Languages": "13 Languages",
        "Console Languages": "3 Languages",
        Reports: "13 Languages",
        Reviews: "Yearly",
      },
    }, */
  }

  const allFeatures = [
    "Unlimited Profiles",
    "Unlimited Roles",
    "Internal Mobility",
    "Personalized Upskilling",
    "Soft Skills & Job Skills",
    "Leadership Assessment",
    "LLM/SLM Console Users",
    "Assessment Languages",
    "Console Languages",
    "Reports",
    "Reviews",
  ];

  useEffect(() => {
    const run = async () => {
      setIsLoadingPricing(true);
      try {
        const location = await getLocationByIP();

        const data = await getAllPricingData(location, billingCycle);
        setPricingData(data);
      } catch (e) {
        console.error(e);
        setErrorModal({
          show: true,
          title: t("pricing.errors.loadingPricingTitle"),
          message: t("pricing.errors.loadingPricingMessage"),
          showButton: false,
        });
        setPricingData([]);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    run();
  }, [billingCycle]);

  const handleSubscribe = async (priceId) => {
    setLoadingPriceId(priceId);
    try {
      const response = await getPaymentLink(priceId);
      if (response.paymentLink) {
        window.location.href = response.paymentLink;
      } else {
        setErrorModal({
          show: true,
          title: t("pricing.errors.errorTitle"),
          message:
            response.message || t("pricing.errors.generatePaymentLinkMessage"),
          showButton: false,
        });
      }
    } catch (error) {
      const rawMessage = String(
        error?.message ||
          error?.response?.data?.message ||
          "",
      );
      const isAccessDeniedError =
        /access denied/i.test(rawMessage) &&
        /insufficient permissions/i.test(rawMessage);

      setErrorModal({
        show: true,
        title: t("pricing.errors.errorTitle"),
        message: isAccessDeniedError
          ? "Your account does not have permission to buy a plan. Please contact the organization super admin to upgrade the subscription."
          : error.message || t("pricing.errors.somethingWentWrong"),
        showButton: false,
      });
    } finally {
      setLoadingPriceId(null);
    }
  };

  return (
    <div className="custom-pricing-wrapper">
      {/* Hero Section */}
      <section className="custom-pricing-hero">
        <h1 className="custom-pricing-hero-title">{t("pricing.hero.title")}</h1>
        <p className="custom-pricing-hero-subtitle">
          {t("pricing.hero.subtitle")}
        </p>

        {/* Controls */}
        <div className="custom-pricing-controls">
          <div className="custom-pricing-toggle-group">
            {/*<label className="custom-pricing-label">Billing Cycle:</label>*/}
            <div className="custom-pricing-toggle">
              <button
                className={`custom-pricing-toggle-btn ${billingCycle === "Monthly" ? "active" : ""}`}
                onClick={() => setBillingCycle("Monthly")}
              >
                {t("pricing.billing.monthly")}
              </button>
              <button
                className={`custom-pricing-toggle-btn ${billingCycle === "Yearly" ? "active" : ""}`}
                onClick={() => setBillingCycle("Yearly")}
              >
                {t("pricing.billing.annual")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="custom-pricing-cards-section">
        {isLoadingPricing ? (
          <div className="custom-pricing-loading">
            {" "}
            {t("pricing.loading.pricing")}
          </div>
        ) : pricingData.length > 0 ? (
          <div className="custom-pricing-cards-container">
            {pricingData.map((plan) => (
              <PricingCard
                key={plan.priceId}
                plan={plan}
                isLoading={loadingPriceId === plan.priceId}
                onSubscribe={() => handleSubscribe(plan.priceId)}
                allFeatures={allFeatures}
              />
            ))}
          </div>
        ) : (
          <div className="custom-pricing-error">
            {t("pricing.errors.loadingPricingMessage")}
          </div>
        )}
      </section>

      {/* Comparison Table - KEEP AS IS */}
      <section className="pricing-comparison-section">
        <h2 className="pricing-comparison-title">
          {t("pricing.comparison.title")}
        </h2>
        <div className="pricing-table-wrapper">
          <table className="pricing-table">
            <thead>
              <tr>
                <th className="pricing-table-feature">
                  {" "}
                  {t("pricing.comparison.feature")}
                </th>
                {pricingTiers.map((tier, index) => (
                  <th key={index} className="pricing-table-tier">
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allFeatures.map((feature, index) => (
                <tr key={index} className="pricing-table-row">
                  <td className="pricing-table-feature">
                    {" "}
                    {t(featureTranslationKeys[feature] || feature)}
                  </td>
                  {pricingTiers.map((tier, tierIndex) => (
                    <td key={tierIndex} className="pricing-table-cell">
                      <div className="pricing-table-content">
                        {tier.features[feature] ? (
                          <>
                            {/*<span className="pricing-check">✓</span>*/}
                            {typeof tier.features[feature] !== "string" &&
                              tier.features[feature] !== "true" && (
                                <span className="pricing-check">✓</span>
                              )}
                            {typeof tier.features[feature] === "string" &&
                              tier.features[feature] !== "true" && (
                                <span>{tier.features[feature]}</span>
                              )}
                          </>
                        ) : (
                          <span className="pricing-x">✗</span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        show={errorModal.show}
        onClose={() =>
          setErrorModal({
            show: false,
            title: "",
            message: "",
            showButton: false,
          })
        }
        type="error"
        title={errorModal.title}
        message={errorModal.message}
        showButton={errorModal.showButton}
      />
    </div>
  );
}
